import React, { useState, useEffect } from "react";
import { Download, X, Share2, PlusSquare } from "lucide-react";

export default function InstallPwaBanner() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [visible, setVisible] = useState(false);
    const [isIos, setIsIos] = useState(false);
    const [showIosGuide, setShowIosGuide] = useState(false);

    useEffect(() => {
        // If dismissed recently, don't show
        const dismissed = localStorage.getItem("pwa_install_dismissed");
        if (dismissed && Date.now() - parseInt(dismissed, 10) < 7 * 24 * 60 * 60 * 1000) {
            return;
        }

        // If already in standalone PWA mode, don't show
        const isStandalone =
            window.matchMedia("(display-mode: standalone)").matches ||
            window.navigator.standalone === true ||
            document.referrer.includes("android-app://");

        if (isStandalone) {
            return;
        }

        // Detect iOS
        const ua = window.navigator.userAgent || "";
        const isAppleDevice = /iPad|iPhone|iPod/.test(ua) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(ua));

        if (isAppleDevice) {
            setIsIos(true);
            const timer = setTimeout(() => {
                setVisible(true);
            }, 2500);
            return () => clearTimeout(timer);
        }

        // Android / Chromium beforeinstallprompt handler
        const handleBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setVisible(true);
        };

        const handleAppInstalled = () => {
            setVisible(false);
            setDeferredPrompt(null);
        };

        window.addEventListener("beforeinstallprompt", handleBeforeInstall);
        window.addEventListener("appinstalled", handleAppInstalled);

        return () => {
            window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
            window.removeEventListener("appinstalled", handleAppInstalled);
        };
    }, []);

    const handleInstall = async () => {
        if (isIos) {
            setShowIosGuide(!showIosGuide);
            return;
        }
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === "accepted") {
            setVisible(false);
        }
        setDeferredPrompt(null);
    };

    const handleDismiss = () => {
        setVisible(false);
        localStorage.setItem("pwa_install_dismissed", Date.now().toString());
    };

    if (!visible) return null;

    return (
        <div className="fixed bottom-20 md:bottom-6 left-3 right-3 md:left-auto md:right-6 md:w-96 z-50 bg-[#1C1B1A] text-white p-3.5 rounded-2xl shadow-2xl border border-white/10 animate-fade-up">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <img src="/icons/icon-192x192.png" alt="NeighborNest" className="w-10 h-10 rounded-xl shrink-0 ring-1 ring-white/10" />
                    <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">
                            {isIos ? "Install on iPhone" : "Install NeighborNest"}
                        </p>
                        <p className="text-xs text-white/70 truncate">Fast gate access & push alerts</p>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    <button
                        onClick={handleInstall}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#A76D60] hover:bg-[#8A5A4F] text-white rounded-lg text-xs font-medium transition-colors shadow-xs"
                    >
                        {isIos ? (
                            <>
                                <Share2 size={13} />
                                <span>How to Add</span>
                            </>
                        ) : (
                            <>
                                <Download size={13} />
                                <span>Install</span>
                            </>
                        )}
                    </button>
                    <button
                        onClick={handleDismiss}
                        className="p-1.5 text-white/60 hover:text-white transition-colors"
                        title="Dismiss"
                    >
                        <X size={15} />
                    </button>
                </div>
            </div>

            {/* iOS Step-by-Step Instructions Drawer */}
            {isIos && showIosGuide && (
                <div className="mt-3 pt-3 border-t border-white/10 text-xs space-y-2 animate-fade-in bg-white/5 -mx-3.5 -mb-3.5 p-3.5 rounded-b-2xl">
                    <p className="font-semibold text-white/90">2 quick steps on iPhone Safari:</p>
                    <div className="flex items-center gap-2 text-white/80">
                        <span className="w-5 h-5 rounded-full bg-[#A76D60] text-white font-bold text-[10px] grid place-items-center shrink-0">1</span>
                        <span>Tap the <strong>Share</strong> button (📤) at the bottom of Safari.</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/80">
                        <span className="w-5 h-5 rounded-full bg-[#A76D60] text-white font-bold text-[10px] grid place-items-center shrink-0">2</span>
                        <span>Scroll down & tap <strong>"Add to Home Screen"</strong> (➕).</span>
                    </div>
                </div>
            )}
        </div>
    );
}
